import React, { useState } from 'react';
import {
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TablePagination,
  Box,
  Button,
  TextField,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import useApiCaller from 'hooks/apiCaller';

const TableComponent = ({
  tableData,
  setTableData,
  columns,
  tableName,
  onRowsPerPageChange,
  onPageChange,
  count,
  selectedId,
  rowClickedKey = '_id',
  naviagtionPath,
  useWholeRowClicked = true,
  enableLocalSearch = false,
  getRowStyle,
  customComponent = undefined,
}) => {
  const [page, setPage] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const rowsPerPage = 10;
  const navigate = useNavigate();
  const apiCaller = useApiCaller();

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    if (onPageChange) {
      onPageChange(newPage, rowsPerPage); // Parent handles API fetch
    }
  };

  const handleRowClick = (courseId) => {
    if (naviagtionPath) {
      navigate(`${naviagtionPath}${courseId}`);
    } else {
      navigate(`/update-course/${courseId}`, {
        state: { courseId },
      });
    }
  };

  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
  };

  const handleExport = async () => {
    const data = {
      courseId: selectedId,
    };
    try {
      const response = await apiCaller.post('/super-user/report', data, {
        responseType: 'arraybuffer',
      });
      const blob = new Blob([response.data], { type: 'application/octet-stream' });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'report.xlsx');
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.log('Error exporting file:', error);
    }
  };

  const filteredData = tableData.filter((row) =>
    columns.some((column) => {
      const cellValue = row[column.field];
      return (
        typeof cellValue === 'string' && cellValue.toLowerCase().includes(searchQuery.toLowerCase())
      );
    })
  );

  const dataToRender = enableLocalSearch ? filteredData : tableData;

  return (
    <div>
      <Box
        sx={
          tableName === 'Report'
            ? { display: 'flex', justifyContent: 'flex-end', marginBottom: '5px' }
            : { display: 'flex', justifyContent: 'space-between', marginBottom: '10px' }
        }
      >
        {tableName === 'Report' && (
          <Box
            sx={{
              display: 'flex',
              gap: 2,
              marginBottom: 2,
              flexWrap: 'wrap',
              alignItems: 'center',
            }}
          >
            {customComponent && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>{customComponent}</Box>
            )}

            <Button
              variant="contained"
              size="small"
              sx={{
                borderRadius: 20,
                backgroundColor: '#4A90E2',
                color: '#fff',
                fontSize: '12px',
                padding: '6px 16px',
                boxShadow: '0px 3px 6px rgba(0, 0, 0, 0.16)',
                whiteSpace: 'nowrap',
              }}
              onClick={handleExport}
              disabled={!tableData.length}
            >
              Export File
            </Button>
          </Box>
        )}
        {enableLocalSearch && (
          <TextField
            variant="outlined"
            size="small"
            placeholder="Search"
            value={searchQuery}
            onChange={handleSearchChange}
            sx={{ minWidth: { xs: '100%', sm: '300px' } }}
          />
        )}
      </Box>

      <Box sx={{ overflowX: 'auto' }}>
        <Table
          sx={{
            border: '1px solid #e0e0e0',
            borderRadius: '8px',
            boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.1)',
            '& .MuiTableCell-head': {
              backgroundColor: '#f5f5f5',
              color: '#333',
              fontWeight: 'bold',
              fontSize: '14px',
              padding: '6px',
            },
            '& .MuiTableCell-body': {
              fontSize: '13px',
              padding: '6px',
            },
          }}
        >
          <TableHead>
            <TableRow>
              {columns.map((column, index) => (
                <TableCell key={index}>{column.label}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {dataToRender.map((row, rowIndex) => (
              <TableRow
                key={rowIndex}
                onClick={() =>
                  useWholeRowClicked ? handleRowClick(row._id ?? row[rowClickedKey]) : () => {}
                }
                sx={{
                  cursor: 'pointer',
                  '&:hover': {
                    backgroundColor: '#f0f0f0',
                  },
                  ...(getRowStyle ? getRowStyle(row) : {}),
                }}
              >
                {columns.map((column, colIndex) => (
                  <TableCell key={colIndex}>
                    {column.render
                      ? column.render(row)
                      : typeof row[column.field] === 'boolean'
                        ? row[column.field]
                          ? 'Yes'
                          : 'No'
                        : (String(row[column.field]) ?? '-')}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>

      <Box>
        <TablePagination
          component="div"
          count={count ?? dataToRender.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          rowsPerPageOptions={[]}
          labelDisplayedRows={
            count != null && count >= 0
              ? undefined // use default label
              : () => ''
          }
        />
      </Box>
    </div>
  );
};

export default TableComponent;
